import React from 'react';

import {
  ArrowBack as ArrowBackIcon,
  FileDownloadOutlined as FileDownloadIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useNavigate, useParams } from 'react-router';

import { appRoutes, DateFormatPatterns, RouterEntitiesId } from '@clearverify/constants';

import { IntegrationUsageLogStatus, QueryMode } from 'src/api';
import {
  INTEGRATION_USAGE_LOG_ENDPOINT_LABELS,
  INTEGRATION_USAGE_LOG_FILTER_IDS,
  INTEGRATION_USAGE_LOG_STATUS_LABELS,
} from 'src/constants/integrationUsageLog';
import { ModalId } from 'src/constants/modals';
import { useFilterStorage, useSearch, useSearchQueryVariables } from 'src/hooks/common';
import {
  useIntegrationDetail,
  useIntegrationUsageLogStats,
  useIntegrationUsageLogs,
} from 'src/hooks/integration';
import { useModalV2 } from 'src/hooks/shared';
import {
  type ExportFilterSummaryItem,
  ExportIntegrationUsageLogsModal,
} from 'src/modals/apiIntegrations';
import { prepareIntegrationUsageLogsFilter } from 'src/utils/integrationUsageLogs';

import { DateRangeShortcuts } from './DateRangeShortcuts';
import { IntegrationUsageLogsTable } from './IntegrationUsageLogsTable';

type StatCardProps = {
  label: string;
  value: number | undefined;
  loading: boolean;
  color?: string;
};

const StatCard: React.FC<StatCardProps> = ({ label, value, loading, color }) => (
  <Paper variant="outlined" elevation={0} sx={{ p: 2, borderRadius: 2, flex: 1, minWidth: 140 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    {loading || value === undefined ? (
      <Skeleton width={48} height={32} sx={{ mt: 0.5 }} />
    ) : (
      <Typography variant="h5" fontWeight={700} mt={0.5} sx={color ? { color } : undefined}>
        {value.toLocaleString()}
      </Typography>
    )}
  </Paper>
);

export const IntegrationUsageLogsPageWrapper: React.FC = () => {
  const params = useParams<{ [RouterEntitiesId.integration]: string }>();
  const integrationId = params[RouterEntitiesId.integration] ?? '';
  const navigate = useNavigate();

  const { integration, integrationLoading } = useIntegrationDetail(integrationId);
  const { openModal } = useModalV2();
  const { searchValue } = useSearch();

  const searchQueryVariables = useSearchQueryVariables(searchValue =>
    searchValue
      ? {
          OR: [
            { requesterId: { contains: searchValue, mode: QueryMode.Insensitive } },
            { last4Ssn: { contains: searchValue, mode: QueryMode.Insensitive } },
          ],
        }
      : {},
  );

  const { filter, setFilter, sortQueryVariable, paginationQueryVariables, pagination } =
    useFilterStorage();

  const didInitializeFilter = React.useRef(false);
  React.useEffect(() => {
    if (didInitializeFilter.current) return;
    didInitializeFilter.current = true;
    if (Object.keys(filter).length > 0) return;
    setFilter({
      [INTEGRATION_USAGE_LOG_FILTER_IDS.createdAtFrom]: DateTime.now().startOf('month').toISO(),
      [INTEGRATION_USAGE_LOG_FILTER_IDS.createdAtTo]: DateTime.now().endOf('day').toISO(),
    });
  }, [filter, setFilter]);

  const filterQueryVariables = prepareIntegrationUsageLogsFilter(filter, integrationId);

  const statsFilter = React.useMemo(
    () => ({ ...filterQueryVariables, ...searchQueryVariables }),
    [filterQueryVariables, searchQueryVariables],
  );

  const {
    integrationUsageLogsList,
    integrationUsageLogsCount,
    integrationUsageLogsLoading,
    refetch,
  } = useIntegrationUsageLogs(statsFilter, sortQueryVariable, paginationQueryVariables, true);

  const {
    stats,
    loading: statsLoading,
    refetch: refetchStats,
  } = useIntegrationUsageLogStats(statsFilter);

  const refetchData = React.useCallback(async () => {
    await Promise.all([refetch(), refetchStats()]);
    pagination.resetPage();
  }, [pagination, refetch, refetchStats]);

  const filterSummary = React.useMemo(
    () => buildFilterSummary(filter, searchValue),
    [filter, searchValue],
  );

  const handleOpenExport = React.useCallback(() => {
    openModal(ExportIntegrationUsageLogsModal, {
      id: ModalId.ExportIntegrationUsageLogsModal,
      integrationName: integration?.name,
      where: statsFilter,
      filterSummary,
    });
  }, [filterSummary, openModal, statsFilter, integration?.name]);

  return (
    <Stack spacing={3}>
      <Box display="flex" alignItems="center" gap={1.5}>
        <Tooltip title="Back to integrations">
          <IconButton size="small" onClick={() => navigate(appRoutes.apiIntegrations.index)}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box>
          {integrationLoading ? (
            <Skeleton width={240} height={28} />
          ) : (
            <Typography variant="h6" fontWeight={700}>
              {integration?.name ?? 'Integration'}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Usage logs
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleOpenExport}
          disabled={integrationUsageLogsLoading && integrationUsageLogsCount === 0}
          sx={{ ml: 'auto' }}
        >
          Export
        </Button>
      </Box>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <StatCard label="Total requests" value={stats?.total} loading={statsLoading} />
        <StatCard
          label="Verified + Ambiguous"
          value={stats?.matched}
          loading={statsLoading}
          color="success.main"
        />
        <StatCard
          label="No match"
          value={stats?.noMatch}
          loading={statsLoading}
          color="warning.main"
        />
        <StatCard label="Errors" value={stats?.failed} loading={statsLoading} color="error.main" />
      </Stack>

      <DateRangeShortcuts />

      <IntegrationUsageLogsTable
        usageLogsList={integrationUsageLogsList}
        usageLogsCount={integrationUsageLogsCount}
        onRefresh={refetchData}
        pagination={pagination}
        loading={integrationUsageLogsLoading}
      />
    </Stack>
  );
};

const formatFilterDate = (value: unknown): string | null =>
  typeof value === 'string' && value
    ? DateTime.fromISO(value).toFormat(DateFormatPatterns.shortDateWithSlash)
    : null;

const buildFilterSummary = (
  filter: Record<string, unknown>,
  searchValue?: string | null,
): ExportFilterSummaryItem[] => {
  const summary: ExportFilterSummaryItem[] = [];

  const from = formatFilterDate(filter[INTEGRATION_USAGE_LOG_FILTER_IDS.createdAtFrom]);
  const to = formatFilterDate(filter[INTEGRATION_USAGE_LOG_FILTER_IDS.createdAtTo]);
  if (from || to) {
    summary.push({ label: 'Date range', value: `${from ?? '…'} – ${to ?? '…'}` });
  }

  const endpoints = filter[INTEGRATION_USAGE_LOG_FILTER_IDS.endpoint];
  if (Array.isArray(endpoints) && endpoints.length > 0) {
    summary.push({
      label: 'Endpoint',
      value: endpoints
        .map(
          endpoint =>
            (INTEGRATION_USAGE_LOG_ENDPOINT_LABELS as Record<string, string>)[endpoint as string] ??
            endpoint,
        )
        .join(', '),
    });
  }

  const statuses = filter[INTEGRATION_USAGE_LOG_FILTER_IDS.status];
  if (Array.isArray(statuses) && statuses.length > 0) {
    summary.push({
      label: 'Status',
      value: statuses
        .map(
          status =>
            INTEGRATION_USAGE_LOG_STATUS_LABELS[status as IntegrationUsageLogStatus] ?? status,
        )
        .join(', '),
    });
  }

  if (searchValue) {
    summary.push({ label: 'Search', value: searchValue });
  }

  return summary;
};
